/**
 * Service to fetch health check status from task-suite API.
 * Uses GET /task-suite/latest/apipath-testing (the canonical source)
 * rather than the deprecated /healthcheckreports blob endpoint.
 */

import { apiConfig, endpoints } from '../config/api';

export interface HealthCheckStatus {
  [apiPath: string]: {
    isActive: boolean;
    lastChecked?: string;
    message?: string;
  };
}

export interface DeploymentHealthEntry {
  status: string;
  capability: string;
  error?: string;
}

export interface HealthCheckResult {
  summary: HealthCheckStatus;
  deploymentHealth: Record<string, Record<string, DeploymentHealthEntry[]>>;
  updatedAt?: string;
}

/**
 * Response shape from GET /task-suite/latest/apipath-testing
 */
interface TaskSuiteLatestResponse {
  task: string;
  updatedAt: string;
  resultCount: number;
  results: Array<{
    apiPath: string;
    summary: {
      total: number;
      healthy: number;
      failed: number;
      healthPercent: number;
      isActive: boolean;
    };
    deployments: Array<{
      deploymentName: string;
      status: string;
      capability?: string;
      error?: string;
    }>;
    timestamp: string;
  }>;
}

/**
 * Fetches the latest apipath-testing results from task-suite
 * and maps them to the HealthCheckResult format consumed by the UI.
 */
export async function fetchHealthCheckData(getToken: () => Promise<string>): Promise<HealthCheckResult> {
  if (apiConfig.error) {
    throw new Error(apiConfig.error);
  }

  const token = await getToken();
  const response = await fetch(endpoints.taskSuiteLatest('apipath-testing'), {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });

  if (!response.ok) throw new Error(`Health check fetch failed: ${response.status}`);

  const data: TaskSuiteLatestResponse = await response.json();
  const summary: HealthCheckStatus = {};
  const deploymentHealth: Record<string, Record<string, DeploymentHealthEntry[]>> = {};

  for (const apiResult of data.results) {
    const s = apiResult.summary;
    if (!s) continue;
    const entry = {
      isActive: s.isActive,
      lastChecked: apiResult.timestamp || data.updatedAt,
      message: `${s.healthy}/${s.total} healthy`,
    };
    summary[apiResult.apiPath] = entry;
    summary[`/${apiResult.apiPath}`] = entry;

    // Build per-deployment health map
    const depMap: Record<string, DeploymentHealthEntry[]> = {};
    for (const d of apiResult.deployments ?? []) {
      if (!depMap[d.deploymentName]) depMap[d.deploymentName] = [];
      depMap[d.deploymentName].push({
        status: d.status,
        // Surface the capability the health job actually exercised so the UI can
        // fall back to it when /tmds/models reports empty capabilities for a
        // deployment (per-path catalog config drift, tracked separately).
        capability: d.capability ?? '',
        error: d.error ?? undefined,
      });
    }
    deploymentHealth[apiResult.apiPath] = depMap;
    deploymentHealth[`/${apiResult.apiPath}`] = depMap;
  }

  return { summary, deploymentHealth, updatedAt: data.updatedAt };
}

/**
 * Gets the status for a specific API path
 */
export function getStatusForApi(healthCheckData: HealthCheckStatus, apiPath: string): {
  isActive: boolean;
  label: string;
  lastChecked?: string;
} {
  const normalized = apiPath.replace(/^\//, '');
  const data = healthCheckData[normalized] || healthCheckData[apiPath] || healthCheckData[`/${normalized}`];

  if (!data) {
    return { isActive: false, label: 'Unknown' };
  }

  return {
    isActive: data.isActive,
    label: data.message || (data.isActive ? 'Healthy' : 'Unhealthy'),
    lastChecked: data.lastChecked,
  };
}
